import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import CreateProjectModal from './CreateProjectModal';

const ProjectSelector: React.FC = () => {
  const { projects, currentProject, setCurrentProject } = useProject();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event: PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    const handleWindowScroll = () => {
      // only close if scroll is from window, not inside the dropdown
      if (window.scrollY !== 0) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside, true);
    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside, true);
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [showDropdown]);

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center space-x-2 bg-dusky-purple hover:bg-medium-purple px-4 py-2 rounded-lg transition-colors"
        >
          <div>
            <p className="text-sm font-medium">
              {currentProject?.name || 'Select Project'}
            </p>
            {currentProject?.client_name && (
              <p className="text-xs text-gray-300">{currentProject.client_name}</p>
            )}
          </div>
          <ChevronDown className="w-4 h-4" />
        </button>

        {showDropdown && (
          <div ref={dropdownRef} className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg py-2 z-50">
            <div className="px-4 py-2 border-b border-gray-200">
              <button
                onClick={() => {
                  setShowCreateModal(true);
                  setShowDropdown(false);
                }}
                className="flex items-center space-x-2 text-electric-blue hover:text-darker-blue"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">New Project</span>
              </button>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => {
                    setCurrentProject(project);
                    setShowDropdown(false);
                    navigate('/app/sop-builder');
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                    currentProject?.id === project.id ? 'bg-light-aqua' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{project.name}</p>
                      {project.client_name && (
                        <p className="text-xs text-gray-500">{project.client_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-electric-blue">{project.progress}%</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </>
  );
};

export default ProjectSelector;